import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PhotoUpload from "../components/PhotoUpload";
import GpsField from "../components/GpsField";
import { toast } from "sonner";
import { ArrowLeft, Plus, X } from "lucide-react";

const DEFAULT_TOPICS = [
  "Hygiene & Sanitation", "Maternal & Child Health", "Nutrition", "Family Planning",
  "Vaccination", "Communicable Diseases", "Non-Communicable Diseases", "Adolescent Health",
  "Mental Health", "First Aid",
];

export default function AwarenessForm() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const { user } = useAuth();
  const isFaculty = user?.role === "faculty" || user?.role === "manager";
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [topics, setTopics] = useState(DEFAULT_TOPICS);
  const [villages, setVillages] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [newTopic, setNewTopic] = useState("");

  const [form, setForm] = useState({
    title: "",
    topic: "Hygiene & Sanitation",
    village: "",
    program_date: new Date().toISOString().slice(0, 10),
    attendees: 0,
    description: "",
    remarks: "",
    photo_paths: [],
    latitude: null,
    longitude: null,
    location_label: "",
  });

  useEffect(() => {
    api.get("/topics?category=awareness").then((r) => {
      const fromApi = r.data.map((t) => t.name);
      const merged = Array.from(new Set([...DEFAULT_TOPICS, ...fromApi]));
      setTopics(merged);
    }).catch(() => {});
    api.get("/villages").then((r) => setVillages(r.data.map((v) => v.name))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/awareness/${editId}`)
      .then((r) => {
        const a = r.data;
        setForm({
          title: a.title || "",
          topic: a.topic || "Hygiene & Sanitation",
          village: a.village || "",
          program_date: a.program_date || new Date().toISOString().slice(0, 10),
          attendees: a.attendees ?? 0,
          description: a.description || "",
          remarks: a.remarks || "",
          photo_paths: a.photo_paths || [],
          latitude: a.latitude ?? null,
          longitude: a.longitude ?? null,
          location_label: a.location_label || "",
        });
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, [isEdit, editId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, attendees: parseInt(form.attendees, 10) || 0 };
      if (isEdit) {
        await api.put(`/awareness/${editId}`, payload);
        toast.success("Awareness program updated");
        navigate(`/awareness/${editId}`);
      } else {
        await api.post("/awareness", payload);
        toast.success("Awareness program saved");
        navigate("/awareness");
      }
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const addTopic = async () => {
    const name = newTopic.trim();
    if (!name) return;
    try {
      await api.post("/topics", { name, category: "awareness" });
      setTopics(Array.from(new Set([...topics, name])));
      setForm({ ...form, topic: name });
      setNewTopic("");
      toast.success("Topic added");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-up">
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/awareness"))}
        className="inline-flex items-center gap-2 h-11 px-4 rounded-md border-2 border-brand-primary bg-white text-brand-primary font-bold text-sm shadow-sm hover:bg-brand-primary hover:text-white transition-colors"
        data-testid="back-btn"
        aria-label="Back to previous screen"
      >
        <ArrowLeft className="h-5 w-5" strokeWidth={3} /> Back
      </button>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">{isEdit ? "Edit program" : "New Program"}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Awareness Program</h1>
      </div>

      {loading ? <div className="text-brand-muted">Loading…</div> : (
      <form onSubmit={submit} className="space-y-5" data-testid="awareness-form">
        <Card>
          <Grid>
            <Field label="Program Title *">
              <Input required value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="aw-title" />
            </Field>
            <Field label="Topic *">
              <div className="flex gap-2">
                <Select value={form.topic} onChange={(e) => update("topic", e.target.value)} data-testid="aw-topic">
                  {topics.map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
                {isFaculty && (
                  <button type="button" onClick={() => setManageOpen(true)} className="h-12 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium inline-flex items-center gap-1" data-testid="manage-topics-btn">
                    <Plus className="h-4 w-4" /> Add
                  </button>
                )}
              </div>
            </Field>
            <Field label="Village / Location *">
              {villages.length > 0 ? (
                <Select required value={form.village} onChange={(e) => update("village", e.target.value)} data-testid="aw-village">
                  <option value="">Choose village…</option>
                  {villages.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
              ) : (
                <Input required value={form.village} onChange={(e) => update("village", e.target.value)} data-testid="aw-village" />
              )}
            </Field>
            <Field label="Date *">
              <Input type="date" required value={form.program_date} onChange={(e) => update("program_date", e.target.value)} data-testid="aw-date" />
            </Field>
            <Field label="Attendees">
              <Input type="number" min={0} value={form.attendees} onChange={(e) => update("attendees", e.target.value)} data-testid="aw-attendees" />
            </Field>
            <Field label="Venue / Location Label">
              <Input value={form.location_label} onChange={(e) => update("location_label", e.target.value)} placeholder="Anganwadi, School, Community hall" />
            </Field>
            <Field label="Description" className="md:col-span-2">
              <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="What was covered, target group…" />
            </Field>
            <Field label="Remarks" className="md:col-span-2">
              <Textarea value={form.remarks} onChange={(e) => update("remarks", e.target.value)} placeholder="Observations, follow-ups, feedback…" />
            </Field>
            <Field label="Photos (max 5)" className="md:col-span-2">
              <PhotoUpload value={form.photo_paths} onChange={(v) => update("photo_paths", v)} maxCount={5} testId="aw-photos" />
            </Field>
            <Field label="Geo-Tag Location" className="md:col-span-2">
              <GpsField latitude={form.latitude} longitude={form.longitude} onChange={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))} />
            </Field>
          </Grid>
        </Card>

        <div className="sticky bottom-20 md:bottom-4 z-10">
          <button type="submit" disabled={saving} className="w-full md:w-auto h-12 px-8 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors disabled:opacity-60" data-testid="aw-submit">
            {saving ? "Saving…" : isEdit ? "Update Program" : "Save Program"}
          </button>
        </div>
      </form>
      )}

      {manageOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setManageOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-md p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold">Add Topic</h2>
              <button onClick={() => setManageOpen(false)} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-brand-muted mt-2">Add a custom awareness topic to your domain list.</p>
            <div className="mt-3 flex gap-2">
              <Input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder="e.g. Diabetes Awareness" data-testid="new-topic-input" />
              <button type="button" onClick={addTopic} className="h-12 px-4 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium" data-testid="new-topic-add">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ children }) { return <div className="bg-white border border-brand-border rounded-lg p-5 sm:p-6">{children}</div>; }
function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children, className = "" }) {
  return <div className={className}><label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label><div className="mt-1.5">{children}</div></div>;
}
function Input(props) { return <input {...props} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none transition-all" />; }
function Select({ children, ...props }) { return <select {...props} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none">{children}</select>; }
function Textarea(props) { return <textarea {...props} rows={3} className="w-full px-3 py-2 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none resize-y" />; }
